import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, X } from 'lucide-react';

const emptyMC = () => ([
  { option_id: 'opt-1', label: '', weight: '' },
  { option_id: 'opt-2', label: '', weight: '' },
  { option_id: 'opt-3', label: '', weight: '' },
  { option_id: 'opt-4', label: '', weight: '' },
]);

const emptyTF = () => ([
  { option_id: 'opt-1', label: 'True', weight: '' },
  { option_id: 'opt-2', label: 'False', weight: '' },
]);

const LETTERS = ['A', 'B', 'C', 'D'];

/** The question block from Frank's sketch: dimension, type, text, then options with weights. */
export const QuestionEditor = ({ reference, editing, onSubmit, onCancel, saving, disabled }) => {
  const [dimension, setDimension] = useState(reference.dimensions[0]?.key || '');
  const [type, setType] = useState('multiple_choice');
  const [text, setText] = useState('');
  const [answers, setAnswers] = useState(emptyMC());

  useEffect(() => {
    if (editing) {
      setDimension(editing.dimension);
      setType(editing.type);
      setText(editing.text);
      setAnswers(editing.answers.map((a) => ({ ...a, weight: String(a.weight) })));
    } else {
      setText('');
      setAnswers((prev) => (prev.length === 2 ? emptyTF() : emptyMC()));
    }
  }, [editing]);

  const changeType = (next) => {
    setType(next);
    setAnswers(next === 'true_false' ? emptyTF() : emptyMC());
  };

  const reset = () => {
    setText('');
    setAnswers(type === 'true_false' ? emptyTF() : emptyMC());
  };

  const setAnswer = (index, patch) =>
    setAnswers(answers.map((a, i) => (i === index ? { ...a, ...patch } : a)));

  const filled = answers.filter((a) => a.label.trim() !== '' && String(a.weight).trim() !== '');
  const weights = filled.map((a) => parseFloat(a.weight));
  const allSameWeight = weights.length > 1 && Math.min(...weights) === Math.max(...weights);
  const enoughOptions = type === 'true_false' ? filled.length === 2 : filled.length >= 2;
  const canSubmit = text.trim() !== '' && enoughOptions && !allSameWeight && !disabled;

  const submit = async () => {
    const ok = await onSubmit({ dimension, type, text: text.trim(), answers: filled });
    if (ok && !editing) reset();
  };

  return (
    <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-5"
         data-testid="question-editor">
      <h4 className="text-base font-semibold text-white">
        {editing ? `Edit question ${editing.order}` : 'Add a question'}
      </h4>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">
            Dimension
          </label>
          <select
            value={dimension}
            onChange={(e) => setDimension(e.target.value)}
            data-testid="question-dimension-select"
            className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm
                       text-white focus:outline-none focus:border-cyan-400"
          >
            {reference.dimensions.map((d) => (
              <option key={d.key} value={d.key}>{d.name}</option>
            ))}
          </select>
        </div>
        <div>
          <span className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">
            Question type
          </span>
          <div className="flex items-center gap-4 pt-1.5">
            {reference.question_types.map((t) => (
              <label key={t.value} className="flex items-center gap-2 text-sm text-gray-200
                                              cursor-pointer">
                <input
                  type="radio"
                  name="question-type"
                  checked={type === t.value}
                  onChange={() => changeType(t.value)}
                  data-testid={`question-type-${t.value}`}
                  className="accent-cyan-500"
                />
                {t.label}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">
          Question
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          data-testid="question-text-input"
          placeholder="What the participant reads"
          className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm
                     text-white focus:outline-none focus:border-cyan-400"
        />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs uppercase tracking-wide
                        text-gray-500 mb-1.5">
          <span>Answer options</span>
          <span className="w-24 text-right">Weight</span>
        </div>
        <div className="space-y-2">
          {answers.map((answer, index) => (
            <div key={answer.option_id} className="flex items-center gap-2">
              <span className="w-5 text-sm text-gray-500">
                {type === 'true_false' ? '' : `${LETTERS[index]}.`}
              </span>
              <input
                value={answer.label}
                onChange={(e) => setAnswer(index, { label: e.target.value })}
                data-testid={`question-option-label-${index}`}
                placeholder={type === 'true_false' ? '' : 'Answer text'}
                readOnly={type === 'true_false'}
                className={`flex-1 bg-gray-800 border border-gray-600 rounded-md px-3 py-1.5
                           text-sm text-white focus:outline-none focus:border-cyan-400
                           ${type === 'true_false' ? 'opacity-70' : ''}`}
              />
              <input
                type="number"
                step="0.01"
                value={answer.weight}
                onChange={(e) => setAnswer(index, { weight: e.target.value })}
                data-testid={`question-option-weight-${index}`}
                className="w-24 bg-gray-800 border border-gray-600 rounded-md px-3 py-1.5
                           text-sm text-right text-white focus:outline-none focus:border-cyan-400"
              />
              {type === 'multiple_choice' && answers.length > 2 && (
                <button
                  onClick={() => setAnswers(answers.filter((_, i) => i !== index))}
                  data-testid={`question-option-remove-${index}`}
                  className="text-gray-500 hover:text-red-400 transition-colors"
                  title="Remove this option"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {type === 'multiple_choice' && answers.length < 4 && (
          <button
            onClick={() => setAnswers([...answers, {
              option_id: `opt-${answers.length + 1}`, label: '', weight: '',
            }])}
            data-testid="question-add-option-btn"
            className="mt-2 text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> Add an option
          </button>
        )}
        {allSameWeight && (
          <p className="mt-2 text-xs text-amber-300" data-testid="question-same-weight-warning">
            Every option carries the same weight, so this question could not change a score.
          </p>
        )}
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Button
          onClick={submit}
          disabled={!canSubmit || saving}
          data-testid="question-save-btn"
          className="bg-cyan-600 hover:bg-cyan-700 text-white"
        >
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Add question'}
        </Button>
        {editing && (
          <button onClick={onCancel} data-testid="question-cancel-btn"
                  className="text-sm text-gray-400 hover:text-gray-200 transition-colors">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default QuestionEditor;
